// What the local-agent panels say (docs/superpowers/specs/2026-10-23-relay-pr-connections-design.md, 2026-11-01-subjects-design.md):
// the grant categories, an agent's presence, the PR-first paste string and
// the plugin's install commands, which docs/remote-relay.md gives too.
import type { RelayCategory } from '../api/relay-grants';
import type { RelayPresence } from '../api/relay';
import { KIND_NAMES, parseSubject, type SubjectKey, type SubjectKind } from '../api/relaySubject';
import { prPath } from '../router';

/** Each category as the person chooses it, with what the agent reads under it. */
export const CATEGORY_TEXT: Readonly<Record<RelayCategory, { label: string; detail: string }>> = {
  metadata: { label: 'Description and status', detail: 'title, description, state, branches, labels' },
  comments: { label: 'Comments', detail: 'conversation and review threads' },
  files: { label: 'Changed files', detail: 'paths and line counts' },
  diff: { label: 'Diff', detail: 'the changes themselves' },
  checks: { label: 'Checks', detail: 'check runs and statuses' },
  history: { label: 'History', detail: 'the branch’s commits and force-pushes' },
};

/** What a subject of `kind` is called (relaySubject.ts `KIND_NAMES`), to start a line or label. */
export const kindTitle = (kind: SubjectKind) => KIND_NAMES[kind][0].toUpperCase() + KIND_NAMES[kind].slice(1);
/** What the subject `key` is called: `pull request`, `compare` or `branch`. */
export const kindName = (key: SubjectKey) => KIND_NAMES[parseSubject(key)!.kind];
/** The page of `key` on this Changeling, where it has one: a pull request's; null for a compare or a branch, which have none here yet. */
export function subjectHref(key: SubjectKey): string | null {
  const s = parseSubject(key);
  return s?.kind === 'pull' ? prPath(s.owner, s.repo, s.number) : null;
}

/** The categories of `scope` as words, in their order. */
export function scopeText(scope: readonly RelayCategory[]): string {
  return scope.map((c) => CATEGORY_TEXT[c]?.label.toLowerCase() ?? c).join(', ');
}

/** An agent's presence as one line; `reachable` false when its computer could not be asked. */
export function presenceText(presence: RelayPresence, reachable = true): string {
  if (!reachable) return 'Computer unreachable';
  switch (presence) {
    case 'listening': return 'Agent listening';
    case 'wakeable': return 'Agent can be woken';
    case 'channel': return 'Waiting for the agent to acknowledge';
    case 'connected': return 'Plugin connected, agent not listening';
    default: return 'Agent session offline';
  }
}

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`;
/**
 * When an agent was last heard from (`at`, relayAgents' `seen`), counted
 * from `now`: to the minute within the hour, to the hour within the day,
 * else its date.
 */
export function lastSeenText(at: number | undefined, now: number): string {
  if (at === undefined) return 'Not heard from yet';
  const ago = Math.max(0, now - at);
  if (ago < 60_000) return 'Last seen just now';
  if (ago < 3_600_000) return `Last seen ${plural(Math.floor(ago / 60_000), 'minute')} ago`;
  if (ago < 86_400_000) return `Last seen ${plural(Math.floor(ago / 3_600_000), 'hour')} ago`;
  const when = new Date(at);
  const sameYear = when.getFullYear() === new Date(now).getFullYear();
  return `Last seen ${when.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) })}`;
}

/** How an agent reading a pull request is, for the PR header's agent button. */
export type AgentState = 'listening' | 'connected' | 'offline' | 'unreachable' | 'quiet' | 'checking';
const STATE_TEXT: Readonly<Record<AgentState, string>> = {
  listening: 'listening',
  connected: 'plugin connected, agent not listening',
  offline: 'agent session offline',
  unreachable: 'computer unreachable',
  quiet: 'not heard from in over an hour',
  checking: 'checking',
};
/**
 * The PR header's agent button: `short`, its text; `label`, what it says
 * in full (its accessible name and tooltip, starting with `short`); `tone`, its dot: `live` when
 * an agent listens, `idle` when one is connected but not listening, `off`
 * otherwise, `none` with no agent reading the pull request; `ask` while
 * agents ask to connect (`asking`, their names), which comes first.
 */
export function agentButtonText(agents: readonly { name: string; state: AgentState }[], refused = false, asking: readonly string[] = []): { short: string; label: string; tone: 'live' | 'idle' | 'off' | 'none' | 'ask' } {
  // Waiting on the person comes first.
  if (asking.length === 1) return { short: `Approve ${asking[0]}`, label: `Approve ${asking[0]}: it asks to read this pull request. Open Local agents to check its fingerprint and connect or decline it.`, tone: 'ask' };
  if (asking.length) return { short: `${asking.length} to approve`, label: `${asking.length} to approve: ${asking.join(', ')} ask to read this pull request. Open Local agents to check them and connect or decline each.`, tone: 'ask' };
  if (!agents.length) return refused
    ? { short: 'Agent refused', label: 'Agent refused: your computer refused its grant on this pull request. Open Local agents to grant it again or revoke it.', tone: 'none' }
    : { short: 'Connect agent', label: 'Connect agent: no local agent reads this pull request. Open Local agents to connect one.', tone: 'none' };
  const tone = agents.some((a) => a.state === 'listening') ? 'live' : agents.some((a) => a.state === 'connected') ? 'idle' : 'off';
  const short = agents.length === 1 ? agents[0].name : `${agents.length} agents`;
  const each = agents.length === 1 ? STATE_TEXT[agents[0].state] : agents.map((a) => `${a.name}, ${STATE_TEXT[a.state]}`).join('; ');
  // The accessible name starts with the visible text.
  return { short, label: `${short}: ${each}. Open Local agents to see status, revoke or connect another.`, tone };
}

/**
 * Where the changeling plugin installs from:
 * - `download`: the plugin as this Changeling serves it (`path` on its own
 *   origin, web/src/dev/relayBundle.ts), unpacked into `dir`, which holds
 *   the plugin's own marketplace (Claude Code reads the plugin from there,
 *   so it stays);
 * - `checkout`: that marketplace in a checkout of `repo` (contributors);
 * - `published`: a public plugin marketplace.
 */
export type PluginSource =
  | { kind: 'download'; path: string; dir: string; plugin: string }
  | { kind: 'checkout'; repo: string; marketplace: string; plugin: string }
  | { kind: 'published'; marketplace: string; plugin: string };
/** The plugin download this Changeling serves. */
export const DOWNLOAD: PluginSource = { kind: 'download', path: '/remote-relay/changeling.zip', dir: '~/.local/share/changeling/plugin', plugin: 'changeling@changeling-local' };
/** A checkout of this repository, which is private: for its contributors. */
export const CHECKOUT: PluginSource = { kind: 'checkout', repo: 'https://github.com/empathic/changelist', marketplace: './plugins/changeling', plugin: 'changeling@changeling-local' };
/** The `empathic/tools` marketplace, which the plugin is not published to yet. */
export const PUBLISHED: PluginSource = { kind: 'published', marketplace: 'empathic/tools', plugin: 'changeling@tools' };
/**
 * Where the app tells people and agents to install the plugin from: every
 * install instruction is built from this, and docs/remote-relay.md's
 * "Install" gives the same (logic/relayText.test.ts holds them to it).
 */
export const SOURCE: PluginSource = DOWNLOAD;

/** What the install commands need, under them. */
export const INSTALL_NEEDS = 'These are commands for a POSIX shell (on Windows, run them in WSL or Git Bash, where your agent runs). They need curl, unzip, Node 22 or newer and npm, on macOS with Apple silicon, Linux or Windows (not Intel Macs). Restart the agent after installing.';

/** What `plugin marketplace add` takes for `source`. */
const marketplace = (source: PluginSource) => (source.kind === 'download' ? source.dir : source.marketplace);
/** Whether a download from `origin` can't be altered on the way: HTTPS, or this computer. */
function secure(origin: string): boolean {
  try {
    const { protocol, hostname } = new URL(origin);
    if (protocol === 'https:') return true;
    return protocol === 'http:' && (hostname === 'localhost' || hostname.endsWith('.localhost') || hostname === '[::1]' || /^127(\.\d{1,3}){3}$/.test(hostname));
  } catch {
    return false;
  }
}
/** Why a download source gives way to the checkout on a plain-HTTP origin. */
const PLAIN_HTTP = 'This Changeling isn’t served over HTTPS, so the plugin isn’t downloaded from it, where it could be altered on the way. Install it from a checkout instead.';
/** The source used from `origin`: a download only from a secure one, else the checkout, with why. */
function from(origin: string, source: PluginSource): { source: PluginSource; why: string | null } {
  return source.kind === 'download' && !secure(origin) ? { source: CHECKOUT, why: PLAIN_HTTP } : { source, why: null };
}
/**
 * The install commands for `source`, from the Changeling at `origin`:
 * the step that fetches the marketplace first (none for a published one),
 * then each harness's; and, when a download gave way to the checkout
 * (a plain-HTTP origin), why.
 */
export function install(origin: string, wanted: PluginSource = SOURCE): { fetch: { intro: string; command: string } | null; harnesses: readonly { harness: string; command: string }[]; why: string | null } {
  const { source, why } = from(origin, wanted);
  return { ...commands(origin, source), why };
}
function commands(origin: string, source: PluginSource): { fetch: { intro: string; command: string } | null; harnesses: readonly { harness: string; command: string }[] } {
  const m = marketplace(source);
  const harnesses = [
    { harness: 'Claude Code', command: `claude plugin marketplace add ${m} && claude plugin install ${source.plugin}` },
    { harness: 'Codex', command: `codex plugin marketplace add ${m} && codex plugin add ${source.plugin}` },
  ];
  switch (source.kind) {
    case 'download': {
      const parent = source.dir.slice(0, source.dir.lastIndexOf('/'));
      const leaf = source.dir.slice(source.dir.lastIndexOf('/') + 1);
      return {
        fetch: {
          intro: `Download the plugin from this Changeling and unpack it into ${source.dir}, which it stays in:`,
          // In a subshell: the person's shell stays where it was.
          command: `(mkdir -p ${parent} && cd ${parent} && curl -fsSLO ${origin}${source.path} && rm -rf ${leaf} && unzip -q changeling.zip -d ${leaf} && rm changeling.zip)`,
        },
        harnesses,
      };
    }
    case 'checkout':
      return { fetch: { intro: `Clone ${source.repo}, or go to the root of your checkout:`, command: `git clone ${source.repo} && cd ${source.repo.split('/').at(-1)}` }, harnesses };
    case 'published':
      return { fetch: null, harnesses };
  }
}

/** How an agent installs the plugin from the Changeling at `origin`, in words, for the instruction the person gives it. */
export function agentInstall(origin: string, wanted: PluginSource = SOURCE): string {
  const { source } = from(origin, wanted);
  switch (source.kind) {
    case 'download': return `download ${origin}${source.path}, unpack it into ${source.dir} (replacing what is there), add that directory as a plugin marketplace and install ${source.plugin} from it`;
    case 'checkout': return `install ${source.plugin} from the plugin marketplace at ${source.marketplace.replace(/^\.\//, '')} in a checkout of ${source.repo} (clone it if there is none)`;
    case 'published': return `install ${source.plugin} from the ${source.marketplace} plugin marketplace`;
  }
}

/** What the person pastes into their agent for a request `req` (`XXXX-XXXX`) started on the subject at `url`: `what` names its kind (`PR`, `compare`). */
export function pasteString(url: string, req: string, what = 'PR'): string {
  return `Connect to Changeling ${what} ${url} with the changeling plugin (request ${req}).`;
}
